/**
 * SyncErrorBadge
 *
 * Header / sidebar status pill showing the count of sync_queue rows in
 * `failed` or `conflict` state. Hidden when zero.
 *
 * Mounted in the sidebar bottom area. Click → simple popover with the
 * breakdown (pending / syncing / conflict / failed). Resolution itself
 * happens in the ConflictModal, not here.
 */

import React, { useState, useRef, useEffect } from "react";

import { useSyncStatus } from "../../hooks/useSyncStatus";
import { db } from "../../db/schema";

interface SyncErrorBadgeProps {
  /** When true, render the pill with text label; when false, render compact icon only. */
  expanded: boolean;
}

export const SyncErrorBadge: React.FC<SyncErrorBadgeProps> = ({ expanded }) => {
  const counts = useSyncStatus();
  const [open, setOpen]         = useState(false);
  const [clearing, setClearing] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const clearFailed = async () => {
    setClearing(true);
    try {
      const ids = await db.sync_queue
        .where("status").equals("failed")
        .primaryKeys();
      await db.sync_queue.bulkDelete(ids as string[]);
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        popRef.current && !popRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (counts.errors === 0 && counts.pending === 0 && counts.syncing === 0) {
    return null;
  }

  const hasErrors = counts.errors > 0;

  // Color: red if any errors/conflicts, amber while syncing, slate while pending only.
  const tone = hasErrors
    ? "bg-red-50 text-red-700 border-red-200"
    : counts.syncing > 0
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-slate-50 text-slate-700 border-slate-200";

  const label = hasErrors
    ? `${counts.errors} sync error${counts.errors === 1 ? "" : "s"}`
    : counts.syncing > 0
      ? "Syncing…"
      : `${counts.pending} pending`;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        title={label}
        aria-label={label}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${tone}`}
      >
        <span className={`h-2 w-2 rounded-full ${
          hasErrors ? "bg-red-500" : counts.syncing > 0 ? "bg-amber-500 animate-pulse" : "bg-slate-400"
        }`} />
        {expanded && <span className="truncate">{label}</span>}
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute bottom-full left-0 mb-2 w-56 rounded-xl bg-white shadow-lg border border-slate-200 p-3 z-[80]"
        >
          <div className="text-xs font-semibold text-slate-700 mb-2">Sync queue</div>
          <dl className="space-y-1 text-xs">
            <Row label="Pending"   value={counts.pending}  />
            <Row label="Syncing"   value={counts.syncing}  />
            <Row label="Conflicts" value={counts.conflict} highlight={counts.conflict > 0} />
            <Row label="Failed"    value={counts.failed}   highlight={counts.failed > 0} />
          </dl>
          {counts.conflict > 0 && (
            <p className="mt-2 text-[11px] text-slate-500">
              Conflicts will open a resolution dialog automatically.
            </p>
          )}
          {counts.failed > 0 && (
            <button
              onClick={clearFailed}
              disabled={clearing}
              className="mt-2 w-full rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {clearing ? "Clearing…" : "Clear failed entries"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value: number; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="flex justify-between">
    <dt className="text-slate-600">{label}</dt>
    <dd className={highlight ? "text-red-600 font-medium" : "text-slate-800"}>{value}</dd>
  </div>
);

export default SyncErrorBadge;
